import { computed, Injectable, signal } from "@angular/core";
import { HttpClient, HttpEventType, HttpEvent } from "@angular/common/http";
import { Observable, Subject, EMPTY } from "rxjs";
import { catchError, map, takeUntil, tap } from "rxjs/operators";
import {
  SseEventType,
  SseStartData,
  SseStageData,
  SseBlockStartData,
  SseBlockDeltaData,
  SseBlockCompleteData,
  SseDoneData,
} from "../models/sse.model";
import {
  // ChatSession,
  IChatMessages,
  SessionResponse,
} from "../models/session.model";
import {
  DashboardContext,
  DashboardType,
} from "../models/dashboard-context.model";
import { environment } from "../../../../../environments/environment";

export interface ParsedSseEvent {
  event: SseEventType;
  data:
    | SseStartData
    | SseStageData
    | SseBlockStartData
    | SseBlockDeltaData
    | SseBlockCompleteData
    | SseDoneData
    | any;
}

export interface StreamPayload {
  prompt: string;
  model: string;
  session_id?: string | null;
  context?: any;
}

export interface CustomReportWidgetPayload {
  type: "tile" | "chart";
  query?: any;

  size?: number;
}
const FULL_FEATURE_DASHBOARDS: DashboardType[] = [
  "deferred-revenue",
  "points-overview",
  "income-statement",
  "sankeychart",
  "partner",
  "kpis",
];

@Injectable({ providedIn: "root" })
export class AiChatService {
  private readonly host = `${environment.host()}ai/chats/`;
  private readonly reportsHost = `${environment.host()}ai/reports/`;
  private readonly customReportChangedSubject = new Subject<void>();
  readonly customReportChanged$ = this.customReportChangedSubject.asObservable();

  constructor(private http: HttpClient) {}
  dashboardContext = signal<DashboardContext | null>(null);

  readonly contextPayload = computed(() => {
    const ctx = this.dashboardContext();
    if (!ctx) return null;

    const isFullFeature = FULL_FEATURE_DASHBOARDS.includes(ctx.dashboard);

    return {
      dashboard: ctx.dashboard,
      date_range: ctx.date_range,
      ...(isFullFeature && {
        comparison: ctx.comparison ?? null,
        aggregation: ctx.aggregation ?? null,
      }),
    };
  });

  setDashboardContext(context: DashboardContext): void {
    this.dashboardContext.set(context);
  }

  clearDashboardContext(): void {
    this.dashboardContext.set(null);
  }

  getSessions(cursor?: string | null, pageSize: number = 30) {
    let params: Record<string, string> = { page_size: String(pageSize) };
    if (cursor) {
      params["cursor"] = cursor;
    }

    return this.http
      .get<SessionResponse>(`${this.host}sessions/`, {
        params,
        observe: "response", // get the full HttpResponse, not just body
      })
      .pipe(
        map((response) => {
          const nextHeader = response.headers.get("next");
          const nextCursor = nextHeader
            ? new URL(nextHeader).searchParams.get("cursor") // extract cursor param
            : null;

          return {
            sessions: response.body?.sessions ?? [],
            sessionState: response.body?.session_state ?? {
              status: "active",
              code: null,
              message: null,
            },
            nextCursor,
          };
        }),
      );
  }

  getMessages(sessionId: string) {
    return this.http.get<IChatMessages>(
      `${this.host}sessions/${sessionId}/messages/`,
    );
  }

  deleteSession(sessionId: string): Observable<void> {
    return this.http.delete<void>(`${this.host}sessions/${sessionId}/`);
  }

  updateSession(sessionId: string, payload: any) {
    return this.http.patch(`${this.host}sessions/${sessionId}/`, payload);
  }

  addWidgetToCustomReport(payload: CustomReportWidgetPayload) {
    return this.http
      .post(`${this.reportsHost}custom/widgets/`, payload)
      .pipe(tap(() => this.customReportChangedSubject.next()));
  }
  stopStreaming(payload: Object) {
    return this.http.post(`${this.host}stream/stop/`, payload);
  }

  // ── Streaming ───────────────────────────────────────────────────────────────

  streamMessage(payload: StreamPayload): Observable<ParsedSseEvent> {
    const subject = new Subject<ParsedSseEvent>();
    const destroy$ = new Subject<void>();
    // Per-stream cursor — each concurrent stream tracks its own parse offset
    let cursorIndex = 0;

    this.http
      .post(`${this.host}stream/`, payload, {
        observe: "events",
        responseType: "text",
        reportProgress: true,
      })
      .pipe(
        takeUntil(destroy$),
        catchError((err) => {
          subject.error(err);
          return EMPTY;
        }),
      )
      .subscribe((event: HttpEvent<string>) => {
        if (event.type === HttpEventType.DownloadProgress) {
          //Every time a new chunk arrives, Angular fires a DownloadProgress event.
          const partial = (event as any).partialText as string; //partialText is the entire accumulated response so far (not just the new bit — that's why the cursor exists).
          if (partial) {
            const parsed = this.parseSseChunk(partial, cursorIndex);
            cursorIndex = parsed.cursorIndex;
            parsed.events.forEach((e) => subject.next(e)); //Parse it, push each parsed event into the subject
          }
        } else if (event.type === HttpEventType.Response) {
          subject.complete(); //When the server closes the connection, complete the subject (tells subscribers the stream is done)
          destroy$.next();
        }
      });

    return subject.asObservable();
  }

  // ── SSE Parser ──────────────────────────────────────────────────────────────

  private parseSseChunk(
    raw: string,
    cursorIndex: number,
  ): { events: ParsedSseEvent[]; cursorIndex: number } {
    const results: ParsedSseEvent[] = [];
    const newSlice = raw.slice(cursorIndex); // only look at what's new
    if (!newSlice) return { events: results, cursorIndex };

    const blocks = newSlice.split(/\n\n/); //Each message is separated by a blank line (\n\n). So splitting on \n\n gives us individual messages.
    const completeBlocks = blocks.slice(0, -1); // drop the last piece if the server sent event: block_delta\ndata: {"index"; only processes messages that have their terminating \n\n
    let consumed = 0;

    for (const block of completeBlocks) {
      const trimmed = block.trim();
      consumed += block.length + 2; // +2 for the \n\n separator
      if (!trimmed) continue;

      const lines = trimmed.split("\n");
      let eventType: string | null = null;
      let dataStr: string | null = null;

      for (const line of lines) {
        if (line.startsWith("event:"))
          eventType = line.replace("event:", "").trim();
        else if (line.startsWith("data:"))
          dataStr = line.replace("data:", "").trim();
      }

      if (eventType === "ping") continue; // ignore keepalive pings

      if (eventType && dataStr) {
        try {
          const data = JSON.parse(dataStr);
          results.push({ event: eventType as SseEventType, data });
        } catch {
          consumed -= block.length + 2; // undo — don't advance cursor past broken block
          break;
        }
      }
    }

    // Advance the cursor by exactly how many characters were cleanly processed. Next call to parseSseChunk starts from here.
    return { events: results, cursorIndex: cursorIndex + consumed };
  }
}
